const ASSET_RECOVERY_KEY = 'front-v2:asset-recovery';
const ASSET_RECOVERY_COOLDOWN_MS = 60_000;
const ASSET_PREFIX = '/_next-assets/front/';
const failedAssets = new Set<string>();
let recovering = false;
let installed = false;

function errorMessage(error: unknown) {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) return String((error as { message?: unknown }).message || '');
  return '';
}

export function isAssetLoadError(error: unknown) {
  return /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(errorMessage(error));
}

function assetUrl(raw: string) {
  try {
    const url = new URL(raw, window.location.href);
    if (url.origin !== window.location.origin || !url.pathname.startsWith(ASSET_PREFIX) || !/\.(?:m?js|css)$/.test(url.pathname)) return null;
    return url.href;
  } catch { return null; }
}

export function clearAssetRecoveryMarker() {
  if (recovering) return;
  try { window.sessionStorage.removeItem(ASSET_RECOVERY_KEY); } catch { /* Storage is optional. */ }
  failedAssets.clear();
}

function collectAssets(error: unknown) {
  const urls = new Set(failedAssets);
  const messageUrls = errorMessage(error).match(/(?:https?:\/\/|\/_next-assets\/front\/)[^\s"'<>]+/g) || [];
  for (const raw of messageUrls) { const url = assetUrl(raw); if (url) urls.add(url); }
  // Vite's preload links include transitive dependencies, even when WebKit's
  // failed module cache stops them from appearing in the Network inspector.
  document.querySelectorAll<HTMLLinkElement | HTMLScriptElement>('link[rel="modulepreload"], link[rel="stylesheet"], script[type="module"][src]').forEach(node => {
    const url = assetUrl(node instanceof HTMLScriptElement ? node.src : node.href);
    if (url) urls.add(url);
  });
  return [...urls].slice(0, 80);
}

async function refreshAssetCache(urls: string[]) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 5000);
  let cursor = 0;
  try {
    await Promise.all(Array.from({ length: Math.min(4, urls.length) }, async () => {
      while (cursor < urls.length && !controller.signal.aborted) {
        const url = urls[cursor++];
        try {
          // A regular reload can replay Safari's cached modulepreload failure.
          // Reload the HTTP cache first, consume the response, then start a new document.
          const response = await fetch(url, { cache: 'reload', credentials: 'same-origin', mode: 'same-origin', redirect: 'error', signal: controller.signal });
          if (response.ok) await response.arrayBuffer();
        } catch { /* A failed repair still gets one bounded document reload. */ }
      }
    }));
  } finally { window.clearTimeout(timer); }
}

export function recoverFromAssetLoadError(error: unknown, options: { force?: boolean } = {}) {
  if (!isAssetLoadError(error)) return false;
  if (recovering) return true;
  if (!options.force && navigator.onLine === false) return false;
  const now = Date.now(), path = window.location.href;
  try {
    const raw = window.sessionStorage.getItem(ASSET_RECOVERY_KEY);
    let previous: { path?: string; time?: number } | null = null;
    if (raw) { try { previous = JSON.parse(raw); } catch { /* Replace malformed recovery state. */ } }
    if (!options.force && previous?.path === path && now - Number(previous.time || 0) < ASSET_RECOVERY_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(ASSET_RECOVERY_KEY, JSON.stringify({ path, time: now }));
  } catch {
    // Without a persistent guard, automatic reloads could loop in private mode.
    // An explicit user retry is still allowed and never clears login cookies.
    if (!options.force) return false;
  }
  recovering = true;
  const reload = () => {
    try { window.location.reload(); } catch { recovering = false; }
  };
  void refreshAssetCache(collectAssets(error)).then(reload, reload);
  return true;
}

export function installAssetLoadRecovery() {
  if (installed) return;
  installed = true;
  const onResourceError = (event: Event) => {
    const target = event.target;
    const raw = target instanceof HTMLScriptElement ? target.src : target instanceof HTMLLinkElement ? target.href : '';
    const url = raw && assetUrl(raw);
    if (url && failedAssets.size < 80) failedAssets.add(url);
  };
  const onPreloadError = (event: Event) => {
    if (recoverFromAssetLoadError((event as Event & { payload?: unknown }).payload)) event.preventDefault();
  };
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (recoverFromAssetLoadError(event.reason)) event.preventDefault();
  };
  const onWindowError = (event: ErrorEvent) => { recoverFromAssetLoadError(event.error || event.message); };
  window.addEventListener('error', onResourceError, true);
  window.addEventListener('vite:preloadError', onPreloadError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  window.addEventListener('error', onWindowError);
}
